import {Observable, of, Subject, map} from 'rxjs';
import {isEqual, clone} from 'underscore';
import {
	ControlFluentFields,
	ControlOptions,
	ElementStatus,
	FormControlChangedArgs,
	IFormControl,
	ValidationResult, Validator, ValidatorArgs,
} from '../types';

export class FormControl<T = any> implements IFormControl<T> {
	public readonly validators: Validator<T>[] = [];
	protected onChangeEmitter: Subject<FormControlChangedArgs<T>> = new Subject<FormControlChangedArgs<T>>();
	protected lastErrors: ValidationResult = [];
	protected innerValue: T;
	protected innerStatus: ElementStatus = ElementStatus.untouched;
	protected skipComparison: boolean = false;

	constructor(initialValue: T, options?: ControlOptions<T>) {
		this.skipComparison = !!options?.disableValueComparison;
		this.innerValue = initialValue;
		if (options) {
			if (options.validators) {
				this.validators.push(...options.validators);
			}

			if (options.initialValidateStatus !== undefined) {
				this.status = options.initialValidateStatus;
			}
		}
	}

	public validate(silence?: boolean): Observable<ValidationResult> {
		if (!this.validators.length) {
			this.status = ElementStatus.valid;
			return of([]);
		}

		if (this.status === ElementStatus.disabled) {
			return of([]);
		}

		if(!silence) {
			this.status = ElementStatus.pending;
		}

		return new Observable<ValidationResult>(subscriber => {
			let cancelled = false;
			let inner = new Observable<ValidatorArgs<T>>(innerSubscriber => {
				innerSubscriber.next({
					field: {
						currentValue: this.safeValue,
						parentControl: this,
					},
					errors: [],
					cancelled: false,
				});
			});
			for (const validator of this.validators) {
				inner = inner.pipe(map(v => {
					if (cancelled) {
						throw 'cancelled';
					} else {
						return v;
					}
				}));
				inner = validator(inner);
			}

			const subscribe = inner.subscribe(r => {
				this.lastErrors = r.errors;
				if(!silence) {
					this.status = r.errors.length ? ElementStatus.invalid : ElementStatus.valid;
				}
				subscriber.next(r.errors);
				subscriber.complete();
			});
			return {
				unsubscribe() {
					cancelled = true;
					subscribe.unsubscribe();
				},
			};
		});
	}

	public validateWithEmitters(silence?: boolean) {
		return this.validate(silence).subscribe(errors => {
			this.status = errors.length ? ElementStatus.invalid : ElementStatus.valid;
		});
	}


	public get value(): T {
		return this.safeValue;
	}

	public set value(value: T) {
		if (this.skipComparison || !isEqual(value, this.safeValue)) {
			const prev = this.safeValue;
			this.innerValue = clone(value);
			this.emitOnChange('value', {value: prev}, {value: value});
		}
	}

	public get status(): ElementStatus {
		return this.innerStatus;
	}

	public set status(value: ElementStatus) {
		if (value !== this.status) {
			const prev = this.status;
			this.innerStatus = value;
			this.emitOnChange('status', {status: prev}, {status: value});
		}
	}

	protected get safeValue(): T {
		return clone(this.innerValue);
	}

	public hasError(error?: string): boolean {
		if (!error) {
			return !!this.lastErrors.length;
		}
		return this.lastErrors.map(v => v.toLowerCase()).indexOf(error.toLowerCase()) > -1;
	}

	public firstError(): string {
		return this.lastErrors[0];
	}

	public get onChanged(): Observable<FormControlChangedArgs<T>> {
		return this.onChangeEmitter;
	}

	protected emitOnChange(reason: keyof ControlFluentFields<T>, prev: Partial<ControlFluentFields<T>>, curr: Partial<ControlFluentFields<T>>): void {
		const event = {
			reason: reason,
			prevState: {value: this.value, status: this.status, ...prev},
			currState: {value: this.value, status: this.status, ...curr},
		} as FormControlChangedArgs<T>;
		this.onChangeEmitter.next(event);
	}

	public clearValidators() {
		this.validators.length = 0;
	}
}
